import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Alert, Badge, Button, Card, PageHeader, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { LineChart, Meter, PeriodPicker, Stat, UptimeStrip } from './charts';
import { ago, displayState, formatDate, formatDateTime, gb, type Period, percent, type ServerData, sourceName, type TargetDetail } from './data';
import { IncidentList, MonitoringNav, TargetForm, useSummary } from './MonitoringPage';

const back = (
  <Link to="/monitoreo" className="mb-1 inline-block text-sm text-marca underline-offset-4 hover:underline">
    ← {t('mon.titulo')}
  </Link>
);

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="text-right text-slate-900">{children}</dd>
    </div>
  );
}

// --- Ficha de un sitio ---

export function TargetPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<Period>('24h');
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['monitoreo', 'sitio', id, period],
    queryFn: () => api<TargetDetail>(`/api/monitoreo/sitios/${id}?periodo=${period}`),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  const s = q.data;

  async function patch(body: object, msg: string) {
    setError(null);
    try {
      await api(`/api/monitoreo/sitios/${id}`, { method: 'PATCH', json: body });
      setNotice(msg);
      await qc.invalidateQueries({ queryKey: ['monitoreo'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  if (!s) return <div className="space-y-5">{back}{q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>;
  const state = displayState(s);
  const ms = (v: number) => t('mon.ms', { n: Math.round(v) });

  return (
    <div className="space-y-5">
      <PageHeader
        back={back}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {s.name}
            <Badge tone={state.tone}>{state.label}</Badge>
          </span>
        }
        description={
          <a href={s.url} target="_blank" rel="noreferrer noopener" className="break-all underline-offset-4 hover:underline">
            {s.url}
          </a>
        }
        actions={
          s.can.manage && (
            <>
              {s.status === 'activo' ? (
                <>
                  <Button variant="secondary" onClick={() => void patch({ recheck: true }, t('mon.revisar_pedido'))}>
                    {t('mon.revisar_ahora')}
                  </Button>
                  <Button variant="secondary" title={t('mon.pausar_ayuda')} onClick={() => void patch({ status: 'pausado' }, t('mon.guardado'))}>
                    {t('mon.pausar')}
                  </Button>
                </>
              ) : (
                <Button onClick={() => void patch({ status: 'activo' }, t('mon.guardado'))}>{t('mon.vigilar')}</Button>
              )}
              {!editing && (
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  {t('mon.editar')}
                </Button>
              )}
            </>
          )
        }
      />
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {s.status === 'activo' && s.state === 'caido' && s.lastError && <Alert>{s.lastError}</Alert>}
      {editing && (
        <Card title={t('mon.configuracion')}>
          <TargetForm
            target={s}
            onDone={(msg) => {
              setEditing(false);
              setNotice(msg);
            }}
            onCancel={() => setEditing(false)}
          />
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={`${t('mon.disponibilidad')} · ${t('mon.col_24h')}`} value={percent(s.uptime.day, 2)} />
        <Stat label={`${t('mon.disponibilidad')} · ${t('mon.col_7d')}`} value={percent(s.uptime.week, 2)} />
        <Stat label={`${t('mon.disponibilidad')} · ${t('mon.col_30d')}`} value={percent(s.uptime.month, 2)} />
        <Stat
          label={`${t('mon.col_latencia')} · ${t('mon.col_24h')}`}
          value={s.uptime.latencyMs !== null ? ms(s.uptime.latencyMs) : t('mon.sin_datos')}
          sub={s.lastLatencyMs !== null && s.lastCheckedAt ? `${ms(s.lastLatencyMs)} ${ago(s.lastCheckedAt)}` : undefined}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-5">
          <Card>
            <div className="mb-4 flex justify-end">
              <PeriodPicker value={period} onChange={setPeriod} />
            </div>
            <div className="space-y-6">
              <LineChart
                title={t('mon.latencia')}
                period={period}
                points={s.series.map((p) => ({ at: p.at, value: p.latencyMs }))}
                format={ms}
                note={t('mon.latencia_ayuda')}
              />
              <LineChart title={t('mon.disponibilidad')} period={period} points={s.series.map((p) => ({ at: p.at, value: p.uptime }))} max={100} format={(v) => percent(v)} />
            </div>
          </Card>
          <Card>
            <UptimeStrip days={s.days} />
          </Card>
          <Card title={t('mon.incidentes')}>
            <IncidentList incidents={s.incidents} canAcknowledge={s.can.acknowledge} />
          </Card>
        </div>
        <Card variant="aside" title={t('mon.configuracion')} className="self-start">
          <dl className="divide-y divide-slate-200">
            <Row label={t('mon.ultima_revision')}>{s.lastCheckedAt ? formatDateTime(s.lastCheckedAt) : t('mon.sin_datos')}</Row>
            <Row label={t('mon.codigo')}>{s.lastStatusCode ?? t('mon.sin_datos')}</Row>
            {s.lastError && <Row label={t('mon.ultimo_error')}>{s.lastError}</Row>}
            <Row label={t('mon.col_certificado')}>{s.certExpiresAt ? t('mon.vence', { fecha: formatDate(s.certExpiresAt) }) : t('mon.sin_datos')}</Row>
            {s.certIssuer && <Row label={t('mon.emisor')}>{s.certIssuer}</Row>}
            <Row label={t('mon.responsable')}>{s.owner ?? (s.appOwner ? t('mon.responsable_app', { nombre: s.appOwner }) : t('mon.solo_it'))}</Row>
            <Row label={t('mon.aplicacion')}>{s.app?.name ?? t('mon.campo_app_ninguna')}</Row>
            <Row label={t('mon.campo_codigos')}>
              {s.expectedStatusMin}–{s.expectedStatusMax}
            </Row>
            {s.keyword && <Row label={t('mon.campo_texto')}>«{s.keyword}»</Row>}
            <Row label={t('mon.origen')}>
              {sourceName(s.source)}
              {s.missingSince && <span className="block text-xs text-amber-900">{t('mon.ya_no_esta', { origen: sourceName(s.source) })}</span>}
            </Row>
          </dl>
        </Card>
      </div>
    </div>
  );
}

// --- Servidor ---

export function ServerPage() {
  const [period, setPeriod] = useState<Period>('24h');
  const summary = useSummary();
  const q = useQuery({
    queryKey: ['monitoreo', 'servidor', period],
    queryFn: () => api<ServerData>(`/api/monitoreo/servidor?periodo=${period}`),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
  const d = q.data;
  const header = (
    <>
      <PageHeader title={t('mon.titulo')} description={t('mon.descripcion')} />
      <MonitoringNav />
    </>
  );
  if (!d) return <div className="space-y-5">{header}{q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>;
  if (!d.host)
    return (
      <div className="space-y-5">
        {header}
        <Alert tone="info">{t('mon.sin_servidor')}</Alert>
      </div>
    );

  const latest = d.latest;
  const detail = latest?.detail ?? {};
  const pct = (v: number) => percent(v, 0);
  const points = (key: 'cpu' | 'memory' | 'swap' | 'disk' | 'load') => d.series.map((p) => ({ at: p.at, value: p[key] }));
  const hasSwap = d.series.some((p) => p.swap !== null);

  return (
    <div className="space-y-5">
      {header}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-mono text-lg font-semibold text-tinta">{d.host.name}</h2>
          <p className="text-xs text-slate-600">
            {t('mon.host_info', { cpus: d.host.cpuCount ?? '?', ram: gb(d.host.memoryTotalBytes), os: d.host.os ?? '' })}
            {d.host.bootAt && ` · ${t('mon.encendido', { fecha: formatDateTime(d.host.bootAt) })}`}
          </p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {d.host.stale && <Alert tone="warning">{t('mon.servidor_sin_datos')}</Alert>}

      {latest && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Meter label={t('mon.cpu')} value={latest.cpu} sub={latest.load !== null ? `${t('mon.carga')}: ${latest.load}` : undefined} />
          <Meter label={t('mon.memoria')} value={latest.memory} sub={t('mon.de_total', { gb: gb(d.host.memoryTotalBytes) })} />
          {latest.swap !== null && <Meter label={t('mon.swap')} value={latest.swap} />}
          <Meter label={t('mon.disco')} value={latest.disk} sub={t('mon.libres', { gb: gb(latest.diskFreeBytes) })} />
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <LineChart title={t('mon.cpu')} period={period} points={points('cpu')} max={100} format={pct} current={latest ? pct(latest.cpu) : undefined} />
        </Card>
        <Card>
          <LineChart title={t('mon.memoria')} period={period} points={points('memory')} max={100} format={pct} current={latest ? pct(latest.memory) : undefined} />
        </Card>
        {hasSwap && (
          <Card>
            <LineChart title={t('mon.swap')} period={period} points={points('swap')} max={100} format={pct} current={latest?.swap != null ? pct(latest.swap) : undefined} />
          </Card>
        )}
        <Card>
          <LineChart title={t('mon.disco')} period={period} points={points('disk')} max={100} format={pct} current={latest?.disk != null ? pct(latest.disk) : undefined} />
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <Card title={t('mon.incidentes')}>
          <IncidentList incidents={d.incidents} canAcknowledge={summary.data?.can.acknowledge ?? false} />
        </Card>
        <Card variant="aside" title={t('mon.servidor')} className="self-start">
          <dl className="divide-y divide-slate-200">
            {detail.postgres && <Row label={t('mon.postgres')}>{detail.postgres.connections}</Row>}
            {detail.postgres && <Row label={t('mon.base')}>{gb(detail.postgres.sizeBytes)}</Row>}
            {detail.mail && (
              <Row label={t('mon.cola')}>
                {t('mon.cola_detalle', { esperando: detail.mail.waiting, reintentos: detail.mail.delayed, fallidos: detail.mail.failed })}
              </Row>
            )}
            {'backupHours' in detail && (
              <Row label={t('mon.respaldo')}>{detail.backupHours === null || detail.backupHours === undefined ? t('mon.respaldo_sin') : t('mon.respaldo_hace', { h: detail.backupHours })}</Row>
            )}
            {(detail.disks ?? []).slice(1).map((disk) => (
              <Row key={disk.path} label={`${t('mon.disco')} ${disk.path}`}>
                {percent(disk.usedPercent, 0)} · {t('mon.libres', { gb: gb(disk.freeBytes) })}
              </Row>
            ))}
            {latest && <Row label={t('mon.ultima_revision')}>{formatDateTime(latest.sampledAt)}</Row>}
          </dl>
        </Card>
      </div>
    </div>
  );
}
